import { useCallback, useEffect, useState } from 'react';

/** The trade-detail tab route keys. Locked by tests — labels may change (D-08
 *  renamed Analytics→Economics) but these keys must not. 'recalls' (LR-470) is
 *  a CONDITIONAL tab: only shown/mounted when the trade has booked partial
 *  recalls; a stored 'recalls' for a trade with none falls back to 'analytics'
 *  (see TradeDetailPage's effectiveTab guard). */
export type TradeTab = 'analytics' | 'activity' | 'trade-terms' | 'recalls';

/** localStorage key prefix for the persisted active tab. The full key is
 *  scoped to a single trade — `${PREFIX}:${tradeId}` — so a previously-opened
 *  trade's tab choice does not leak across to the next trade the user opens
 *  (LR-208 r5 H4). NB: there is currently NO eviction of stale per-trade keys —
 *  they accumulate one small entry per visited trade (LR-208 r6 LOW; an LRU
 *  cleanup keyed on this prefix is a possible follow-up, not yet implemented). */
export const TRADE_TAB_STORAGE_KEY_PREFIX = 'lendrail:trade-tab';

const VALID_TABS: readonly TradeTab[] = ['analytics', 'activity', 'trade-terms', 'recalls'];

/** A stored value is only honoured if it still maps to a real tab key (guards
 *  stale / tampered storage). */
function isValidTab(value: unknown): value is TradeTab {
  return typeof value === 'string' && (VALID_TABS as readonly string[]).includes(value);
}

function storageKey(tradeId: number | string): string {
  return `${TRADE_TAB_STORAGE_KEY_PREFIX}:${tradeId}`;
}

function readPersistedTab(tradeId: number | string): TradeTab {
  try {
    const stored = localStorage.getItem(storageKey(tradeId));
    if (isValidTab(stored)) return stored;
  } catch {
    /* localStorage unavailable — use the default */
  }
  return 'analytics';
}

/**
 * Active-tab state persisted to localStorage so the chosen tab survives a
 * reload (COMPONENT-MAP "TabNav"). Per-trade scoping (`lendrail:trade-tab:{id}`)
 * — opening a different trade restores ITS persisted tab (or the default), not
 * the last-clicked tab on the previous trade. Pre-r5 the key was global, so
 * clicking Activity on TRD-42 hijacked the Economics default for every other
 * trade the user opened next (LR-208 r5 H4).
 *
 * Only a valid stored key is restored; anything else (absent, stale, tampered)
 * falls back to 'analytics'. All storage access is guarded so a throwing
 * localStorage (privacy mode / disabled storage) degrades to plain in-memory
 * state instead of crashing the page.
 *
 * Lives in its own module (not TradeTabNavigation.tsx) so that component file
 * exports only a component — keeps react-refresh fast-refresh intact.
 */
export function useTradeTab(
  tradeId: number | string,
): [TradeTab, (tab: TradeTab) => void] {
  const [activeTab, setActiveTab] = useState<TradeTab>(() => readPersistedTab(tradeId));

  // Re-resolve when the visible trade changes — the previous trade's tab
  // choice must not leak into the new trade. Initial-mount also runs once,
  // which is harmless (it re-reads the same key the useState initializer read).
  useEffect(() => {
    setActiveTab(readPersistedTab(tradeId));
  }, [tradeId]);

  const selectTab = useCallback(
    (tab: TradeTab) => {
      setActiveTab(tab);
      try {
        localStorage.setItem(storageKey(tradeId), tab);
      } catch {
        /* persist failed — the tab still switches in memory */
      }
    },
    [tradeId],
  );

  return [activeTab, selectTab];
}
